import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminEmpresaService } from '../../shared/services';
import { 
  Building2, 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  CheckCircle, 
  XCircle,
  Users,
  BookOpen,
  FolderOpen,
  Clapperboard
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface Empresa {
  id: string;
  nome: string;
  cnpj?: string;
  ativo: boolean;
  totalCursos?: number;
  totalTrilhas?: number;
  totalUsuarios?: number;
  dataCriacao: string;
}

export function AdminEmpresasPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [ativoFilter, setAtivoFilter] = useState<string>('');
  const page = 1;

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-empresas', page, search, ativoFilter],
    queryFn: async () => {
      console.log('🔍 [AdminEmpresasPage] Iniciando busca de empresas...');
      const result = await adminEmpresaService.getEmpresas({ 
        page, 
        search: search || undefined, 
        ativo: ativoFilter === '' ? undefined : ativoFilter === 'true'
      });
      console.log('📦 [AdminEmpresasPage] Resultado recebido:', result);
      console.log('📊 [AdminEmpresasPage] Total de empresas:', Array.isArray(result) ? result.length : 0);
      return result;
    },
  });

  // Log de erro se houver
  if (error) {
    console.error('❌ [AdminEmpresasPage] Erro ao carregar empresas:', error);
  }

  // Log dos dados quando mudam
  console.log('🎯 [AdminEmpresasPage] Estado atual:', { 
    isLoading, 
    hasData: !!data, 
    dataType: Array.isArray(data) ? 'Array' : typeof data,
    totalEmpresas: Array.isArray(data) ? data.length : 0,
    empresas: data 
  });

  const deleteMutation = useMutation({
    mutationFn: (empresaId: string) => adminEmpresaService.deleteEmpresa(empresaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-empresas'] });
    },
  });

  const handleDelete = (empresaId: string, empresaNome: string) => {
    if (window.confirm(`Tem certeza que deseja excluir a empresa "${empresaNome}"?`)) {
      deleteMutation.mutate(empresaId);
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Building2 className="w-8 h-8 text-blue-600" />
              Gerenciar Empresas
            </h1>
            <p className="text-gray-600 mt-2">
              Gerencie empresas clientes e conteúdos personalizados
            </p>
          </div>
          <Link
            to="/admin/empresas/nova"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Nova Empresa
          </Link>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome ou CNPJ..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select
              value={ativoFilter}
              onChange={(e) => setAtivoFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos os status</option>
              <option value="true">Ativas</option>
              <option value="false">Inativas</option>
            </select>
          </div>
        </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Empresas</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{data?.length || 0}</p>
              </div>
              <Building2 className="w-10 h-10 text-blue-600 opacity-50" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Empresas Ativas</p>
                <p className="text-2xl font-bold text-green-900 mt-1">
                  {data?.filter((e: Empresa) => e.ativo).length || 0}
                </p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-600 opacity-50" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Empresas Inativas</p>
                <p className="text-2xl font-bold text-red-900 mt-1">
                  {data?.filter((e: Empresa) => !e.ativo).length || 0}
                </p>
              </div>
              <XCircle className="w-10 h-10 text-red-600 opacity-50" />
            </div>
          </div>
        </div>

        {/* Grid de Empresas */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Carregando empresas...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-6 p-6">
                {data?.map((empresa: Empresa) => {
                  console.log('🏢 [AdminEmpresasPage] Renderizando empresa:', {
                    id: empresa.id,
                    nome: empresa.nome,
                    cnpj: empresa.cnpj,
                    totalCursos: empresa.totalCursos,
                    totalTrilhas: empresa.totalTrilhas,
                    totalUsuarios: empresa.totalUsuarios,
                    ativo: empresa.ativo,
                  });
                  
                  return (
                  <div
                    key={empresa.id}
                    className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex gap-6">
                      {/* Ícone */}
                      <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-8 h-8 text-blue-600" />
                      </div>

                      {/* Conteúdo */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-1">
                              {empresa.nome}
                            </h3>
                            {empresa.cnpj && (
                              <p className="text-sm text-gray-600 mb-2">
                                CNPJ: {empresa.cnpj}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/admin/empresas/${empresa.id}/grupos`}
                              className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                              title="Gerenciar Grupos"
                            >
                              <FolderOpen className="w-5 h-5" />
                            </Link>
                            <Link
                              to={`/admin/empresas/${empresa.id}/editar`}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Editar"
                            >
                              <Edit2 className="w-5 h-5" />
                            </Link>
                            <button
                              onClick={() => handleDelete(empresa.id, empresa.nome)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Excluir"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>

                        {/* Badges e Info */}
                        <div className="flex flex-wrap items-center gap-3">
                          {empresa.ativo ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                              <CheckCircle className="w-3 h-3" />
                              Ativa
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                              <XCircle className="w-3 h-3" />
                              Inativa
                            </span>
                          )}

                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                            <BookOpen className="w-3 h-3" />
                            {empresa.totalCursos || 0} cursos
                          </span>

                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                            <BookOpen className="w-3 h-3" />
                            {empresa.totalTrilhas || 0} trilhas
                          </span>

                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">
                            <Users className="w-3 h-3" />
                            {empresa.totalUsuarios || 0} usuários
                          </span>

                          <span className="text-xs text-gray-500">
                            Criada em: {new Date(empresa.dataCriacao).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                        <PublishOnboardingButton empresaId={empresa.id} />
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PublishOnboardingButton({ empresaId }: { empresaId: string }) {
  const [result, setResult] = useState<{ trilhaId?: string; videoPublicado: boolean; nomeTrilha?: string } | null>(null);
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: () => adminEmpresaService.publishOnboarding(empresaId),
    onSuccess: (data) => {
      setError('');
      setResult({
        trilhaId: data.trilhaId,
        videoPublicado: data.videoPublicado,
        nomeTrilha: data.nomeTrilha,
      });
    },
    onError: () => {
      setError('Não foi possível publicar o onboarding desta empresa.');
    },
  });

  return (
    <div className="mt-4">
      {result ? (
        <div className="text-sm text-gray-700">
          <p className="font-medium text-gray-900">{result.nomeTrilha || 'Onboarding publicado'}</p>
          <p className="mt-1">
            {result.videoPublicado
              ? 'O vídeo institucional desta empresa está na aula.'
              : 'A aula está pronta. O vídeo institucional entra quando a comunicação da empresa liberar o arquivo.'}
          </p>
          {result.trilhaId && (
            <Link to={`/trilhas/${result.trilhaId}`} className="inline-block mt-2 text-blue-600 hover:text-blue-700">
              Abrir trilha
            </Link>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-50 disabled:opacity-60"
        >
          <Clapperboard className="w-4 h-4" />
          {mutation.isPending ? 'Publicando...' : 'Publicar onboarding'}
        </button>
      )}
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
}
